import alertService from "../components/alert/alertService";
import apiClient from "../api/axios";
import { handleApiError } from "../api/handleApiError";
import { EvidenceItem, EvidenceListResponseDto } from "../models/Evidence";
import axios, { AxiosResponse } from "axios";
import { ActivityAttachmentDetails } from "../models/Activities";
import { ActivityAttachmentResponseDto } from "../models/dto/response/activity-attachment-response.dto";
import {
  EvidenceFilenameUpdateResponse,
  EvidenceListResponse,
  EvidencePresignedUrlResponseDto,
  EvidencePresignedUrlResult,
} from "../models/dto/response/evidence/evidence-presigned-url-response.dto";
import {
  EvidenceDeleteResponse,
  EvidenceDeleteResults,
} from "../models/dto/response/evidence/evidence-presigned-url-response.dto";

export interface GeneratePresignedUrlDto {
  suborganizationRef: string;
  year: number;
  evidenceFileName: string;
  evidenceFileSize: number;
  evidenceS3FileName?: string;
}

export const EvidenceService = {
  getEvidencesBySubOrgAndYear: async (
    subOrgId: string,
    year: number
  ): Promise<EvidenceItem[]> => {
    try {
      const response: AxiosResponse<EvidenceListResponseDto> =
        await apiClient.get(`/v2/activities/evidences/${subOrgId}/${year}`);

      return response.data.results.evidence;
    } catch (error: any) {
      // silent — no alert for this background fetch
    }

    return [];
  },
  getAttachmentByRef: async (
    attachmentRef: string
  ): Promise<ActivityAttachmentDetails | null> => {
    try {
      alertService.showLoading();
      const response: AxiosResponse<ActivityAttachmentResponseDto> =
        await apiClient.get(`/v2/activities/attachment/${attachmentRef}`);
      return response.data.results;
    } catch (error) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  createPresignedUrls: async (
    payload: GeneratePresignedUrlDto
  ): Promise<EvidencePresignedUrlResult | null> => {
    try {
      const response: AxiosResponse<EvidencePresignedUrlResponseDto> =
        await apiClient.post(`/v2/activities/evidences/presignedurls`, payload);

      return response.data.results;
    } catch (error) {
      handleError(error);
    }
    return null;
  },
  uploadEvidenceToPresignedUrl: async (
    file: File,
    presignedUrl: string
  ): Promise<AxiosResponse> => {
    try {
      return await axios.put(presignedUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });
    } catch (error) {
      // The URL is signed for one key, length and 5-minute window; swallowing this reported an unstored file as stored.
      await alertService.error("Evidence-Upload-Failed", "upload-incomplete");
      (error as any)._handled = true;
      throw error;
    }
  },

  getEvidencesAllSubOrg: async (year: number): Promise<EvidenceItem[]> => {
    try {
      const response: AxiosResponse<EvidenceListResponse> = await apiClient.get(
        `/v2/activities/evidences/${year}`
      );

      return response.data.results.evidence;
    } catch (error) {
      // silent — no alert for this background fetch
    }

    return [];
  },
  renameEvidences: async (
    attachmentRef: string,
    newFileName: string
  ): Promise<string | null> => {
    try {
      const response: AxiosResponse<EvidenceFilenameUpdateResponse> =
        await apiClient.patch(
          `/v2/activities/evidences/${attachmentRef}/filename`,
          { newFileName }
        );

      return response.data.results.evidenceFileName;
    } catch (error) {
      handleError(error);
    }
    return null;
  },
  deleteEvidences: async (attachmentRef: string): Promise<boolean> => {
    try {
      alertService.showLoading();
      await apiClient.patch(
        `/v2/activities/evidences/${attachmentRef}/delete`
      );
      return true;
    } catch (error) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return false;
  },
  mutipleDeleteEvidences: async (
    refIds: string[]
  ): Promise<EvidenceDeleteResults | null> => {
    try {
      alertService.showLoading();
      const response: AxiosResponse<EvidenceDeleteResponse> =
        await apiClient.patch(`/v2/activities/evidences/delete`, { refIds });
      return response.data.results;
    } catch (error) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
};

const handleError = (error: unknown) =>
  handleApiError(error, {
    RESOURCE_ALREADY_IN_USE: "Resource-In-Use",
    FILE_EXTENSION_MISMATCH: "File-Extension-Mismatch",
  });
